'use client'

import React, { useState, useEffect } from 'react'
import {
  Phone, PhoneOff, PhoneIncoming,
  Building2, CalendarCheck, CalendarX2, Hash,
  ChevronDown, ChevronUp,
} from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useCallContext } from '@/contexts/CallContext'
import type { IncomingCall } from '@/api/callApi'

function formatDate(iso: string | null | undefined): string {
  if (!iso) return ''
  try {
    return format(parseISO(iso), 'MMM d')
  } catch {
    return ''
  }
}

// M:SS once we cross a minute, 0:SS before that (e.g. 5 → "0:05", 75 → "1:15").
function formatElapsed(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  const ss = String(s).padStart(2, '0')
  return totalSeconds >= 60 ? `${m}:${ss}` : `0:${ss}`
}

// Up-to-two-letter initials from a display name; "?" when there's no name.
function initialsFrom(name: string | null | undefined): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function InfoItem({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType
  label: string
  value: string | null | undefined
}) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
        <Icon className="h-3 w-3 shrink-0" />
        <span className="truncate">{label}</span>
      </div>
      <p className="text-[13px] font-medium text-foreground truncate mt-0.5">
        {value || '—'}
      </p>
    </div>
  )
}

interface IncomingCallCardProps {
  call: IncomingCall
  onAnswer: (callSid: string) => void
  onDecline: (callSid: string) => void
}

function IncomingCallCard({ call, onAnswer, onDecline }: IncomingCallCardProps) {
  const { t } = useTranslation('callRouting')
  const claimedByOther = !!(call.claimedBy || call.claimedByName)

  // Live elapsed timer, counting up from 0 for the lifetime of this card.
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setElapsed((s) => s + 1), 1000)
    return () => clearInterval(id)
  }, [])

  const checkIn = formatDate(call.checkIn)
  const checkOut = formatDate(call.checkOut)

  return (
    <div
      data-testid="incoming-call-card"
      data-variant="full"
      className={[
        'w-80 rounded-xl border border-border shadow-2xl bg-card overflow-hidden transition-all duration-300',
        claimedByOther ? 'opacity-60' : 'opacity-100',
      ].join(' ')}
    >
      {/* ── Header strip ── */}
      <div
        className={[
          'flex items-center gap-3 px-4 py-3 border-b',
          claimedByOther
            ? 'bg-muted border-border'
            : 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-900/40',
        ].join(' ')}
      >
        {/* Icon badge */}
        <div className="relative shrink-0">
          <div
            className={[
              'h-9 w-9 rounded-full flex items-center justify-center',
              claimedByOther ? 'bg-muted-foreground/15' : 'bg-emerald-600',
            ].join(' ')}
          >
            {claimedByOther ? (
              <PhoneOff className="h-[18px] w-[18px] text-muted-foreground" />
            ) : (
              <PhoneIncoming className="h-[18px] w-[18px] text-white" />
            )}
          </div>
          {!claimedByOther && (
            <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-card animate-ping" />
          )}
        </div>

        {/* Label + caller number */}
        <div className="flex-1 min-w-0">
          <p
            className={[
              'text-[11px] font-medium uppercase tracking-wide',
              claimedByOther ? 'text-muted-foreground' : 'text-emerald-600 dark:text-emerald-400',
            ].join(' ')}
          >
            {claimedByOther
              ? (call.claimedByName
                  ? t('incoming.answeredBy', { name: call.claimedByName })
                  : t('incoming.answered'))
              : t('incoming.title')}
          </p>
          <p className="text-[17px] font-medium text-foreground truncate">
            {call.callerNumber}
          </p>
        </div>

        {/* Right: elapsed timer while ringing. Once claimed, the "Answered by
            [name]" label above already conveys who took it, so no raw-id pill. */}
        {!claimedByOther && (
          <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
            {formatElapsed(elapsed)}
          </span>
        )}
      </div>

      {/* ── Body ── */}
      <div className="p-4 space-y-3">
        {/* Guest / caller identity row */}
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[13px] font-semibold">
            {initialsFrom(call.guestName)}
          </div>
          <div className="min-w-0">
            <p className="text-[14px] font-medium text-foreground truncate">
              {call.guestName || t('incoming.unknownCaller')}
            </p>
            <p className="text-[12px] text-muted-foreground truncate">
              {call.guestName ? t('incoming.confirmedGuest') : t('incoming.noReservation')}
            </p>
          </div>
        </div>

        <div className="border-b border-border" />

        {/* Reservation / property info grid */}
        <div className="grid grid-cols-2 gap-2">
          <InfoItem icon={Building2} label={t('incoming.property')} value={call.listingName} />
          <InfoItem icon={CalendarCheck} label={t('incoming.checkIn')} value={checkIn} />
          <InfoItem icon={CalendarX2} label={t('incoming.checkOut')} value={checkOut} />
          <InfoItem
            icon={Hash}
            label={t('incoming.reservation')}
            value={call.reservationId ? `#${call.reservationId}` : ''}
          />
        </div>

        {/* ── Footer (hidden once claimed) ── */}
        {!claimedByOther && (
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => onDecline(call.callSid)}
              className="flex-1 h-[38px] rounded-[10px] flex items-center justify-center gap-1.5 text-sm font-medium bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
            >
              <PhoneOff className="h-4 w-4" />
              {t('incoming.decline')}
            </button>
            <button
              onClick={() => onAnswer(call.callSid)}
              className="flex-1 h-[38px] rounded-[10px] flex items-center justify-center gap-1.5 text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
            >
              <Phone className="h-4 w-4" />
              {t('incoming.answer')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// 紧凑摘要卡：多路来电同时响铃时用它替代完整详情卡。只保留决定"接不接"
// 所需的三件事——号码、来电人/房源、接听/拒接——把单卡高度从 ~280px 压到
// ~60px，这样多路堆叠才不会顶穿视口。入住/退房/预订号等细节留给接起后的
// 通话详情，响铃这几秒本来也来不及读。
function CompactIncomingCallCard({ call, onAnswer, onDecline }: IncomingCallCardProps) {
  const { t } = useTranslation('callRouting')
  const claimedByOther = !!(call.claimedBy || call.claimedByName)

  // 副标题：客人名 + 房源，两者都缺时退回"无预订"提示。
  const subtitle = [call.guestName, call.listingName].filter(Boolean).join(' · ')

  return (
    <div
      data-testid="incoming-call-card"
      data-variant="compact"
      className={[
        'w-80 rounded-xl border border-border shadow-xl bg-card px-3 py-2.5',
        'flex items-center gap-2.5 transition-all duration-300',
        claimedByOther ? 'opacity-60' : 'opacity-100',
      ].join(' ')}
    >
      <div className="relative shrink-0">
        <div
          className={[
            'h-8 w-8 rounded-full flex items-center justify-center',
            claimedByOther ? 'bg-muted-foreground/15' : 'bg-emerald-600',
          ].join(' ')}
        >
          {claimedByOther ? (
            <PhoneOff className="h-4 w-4 text-muted-foreground" />
          ) : (
            <PhoneIncoming className="h-4 w-4 text-white" />
          )}
        </div>
        {!claimedByOther && (
          <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-card animate-ping" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-medium text-foreground truncate">
          {call.callerNumber}
        </p>
        <p className="text-[11px] text-muted-foreground truncate">
          {claimedByOther
            ? (call.claimedByName
                ? t('incoming.answeredBy', { name: call.claimedByName })
                : t('incoming.answered'))
            : (subtitle || t('incoming.noReservation'))}
        </p>
      </div>

      {/* 图标按钮 + aria-label：紧凑卡宽度放不下文字按钮，红/绿话筒是电话
          UI 的通用语义。已被他人接起则整组隐藏，和完整卡一致。 */}
      {!claimedByOther && (
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={() => onDecline(call.callSid)}
            aria-label={t('incoming.decline')}
            title={t('incoming.decline')}
            className="h-9 w-9 rounded-full flex items-center justify-center bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
          >
            <PhoneOff className="h-4 w-4" />
          </button>
          <button
            onClick={() => onAnswer(call.callSid)}
            aria-label={t('incoming.answer')}
            title={t('incoming.answer')}
            className="h-9 w-9 rounded-full flex items-center justify-center bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
          >
            <Phone className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  )
}

// 默认最多渲染 3 张卡，其余折叠成一行。3 张紧凑卡 + 折叠行 ≈ 260px，
// 在最矮的常见视口（768px）上也远够不到右下角悬浮球。
const MAX_VISIBLE_POPUPS = 3

// 堆叠容器底部要给 CallStatusIcon（fixed right-4，h-12 悬浮球）让位，否则
// 卡片一多就压住悬浮球和它的未接计数徽标（popup 是 z-[9999]，悬浮球只有
// z-40，压上去就是彻底点不到）。悬浮球自身 bottom 会因 ActiveCallBar 从
// 1rem 抬到 4.5rem，这里两档跟着抬，并各留 1.5rem 视觉间隙。
const STACK_BOTTOM_GAP = '5.5rem' // 1rem(悬浮球 bottom) + 3rem(h-12) + 1.5rem
const STACK_BOTTOM_GAP_IN_CALL = '9rem' // 4.5rem + 3rem + 1.5rem

export function IncomingCallPopup() {
  const { t } = useTranslation('callRouting')
  const { incomingCalls, activeCall, answerCall, declineCall } = useCallContext()
  const [expanded, setExpanded] = useState(false)

  const total = incomingCalls.length
  const overflowCount = Math.max(0, total - MAX_VISIBLE_POPUPS)
  // 派生而非用 effect 同步：来电挂断后掉回阈值以内时展开态自动失效，
  // 不会留下一个"+0 more"的空折叠行。
  const isExpanded = expanded && overflowCount > 0
  const visibleCalls = isExpanded ? incomingCalls : incomingCalls.slice(0, MAX_VISIBLE_POPUPS)
  // 单路来电保持原本的完整详情卡；2 路起全部降级紧凑卡。
  const compact = total > 1

  if (total === 0) return null

  return (
    <div
      data-testid="incoming-call-stack"
      // pointer-events-none 保留原行为（容器本身不挡页面点击），滚动仍可用：
      // 滚轮事件落在 pointer-events-auto 的卡片上，再冒泡到这个可滚动祖先。
      className="fixed top-4 right-4 z-[9999] flex flex-col gap-3 pointer-events-none overflow-y-auto"
      style={{ bottom: activeCall ? STACK_BOTTOM_GAP_IN_CALL : STACK_BOTTOM_GAP }}
    >
      {visibleCalls.map((call) => (
        <div key={call.callSid} className="pointer-events-auto shrink-0">
          {compact ? (
            <CompactIncomingCallCard
              call={call}
              onAnswer={(sid) => void answerCall(sid)}
              onDecline={declineCall}
            />
          ) : (
            <IncomingCallCard
              call={call}
              onAnswer={(sid) => void answerCall(sid)}
              onDecline={declineCall}
            />
          )}
        </div>
      ))}

      {overflowCount > 0 && (
        <button
          data-testid="incoming-call-overflow"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={isExpanded}
          className="pointer-events-auto shrink-0 w-80 h-10 rounded-xl border border-border shadow-xl bg-card px-3 flex items-center justify-center gap-1.5 text-[13px] font-medium text-foreground hover:bg-accent transition-colors"
        >
          {isExpanded ? (
            <>
              {t('incoming.showLess')}
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            </>
          ) : (
            <>
              {t('incoming.moreRinging', { count: overflowCount })}
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </>
          )}
        </button>
      )}
    </div>
  )
}
